/**
 * Formatação e rótulos do módulo de Anúncios, compartilhados entre a lista, a
 * página da campanha e o assistente. Sem React aqui.
 */

export const PERIODOS = [
  { value: "today", label: "Hoje" },
  { value: "yesterday", label: "Ontem" },
  { value: "last_7d", label: "Últimos 7 dias" },
  { value: "last_14d", label: "Últimos 14 dias" },
  { value: "last_30d", label: "Últimos 30 dias" },
  { value: "this_month", label: "Este mês" },
  { value: "last_month", label: "Mês passado" },
] as const;

export type Periodo = (typeof PERIODOS)[number]["value"];

/** Status efetivo, no vocabulário do Gerenciador de Anúncios. */
export const STATUS: Record<string, { label: string; className: string }> = {
  ACTIVE: { label: "Ativa", className: "bg-emerald-100 text-emerald-800" },
  PAUSED: { label: "Pausada", className: "bg-slate-100 text-slate-700" },
  ARCHIVED: { label: "Arquivada", className: "bg-slate-100 text-slate-500" },
  IN_PROCESS: { label: "Em processamento", className: "bg-blue-100 text-blue-800" },
  WITH_ISSUES: { label: "Com problema", className: "bg-red-100 text-red-800" },
  CAMPAIGN_PAUSED: { label: "Campanha pausada", className: "bg-slate-100 text-slate-700" },
  ADSET_PAUSED: { label: "Conjunto pausado", className: "bg-slate-100 text-slate-700" },
  PENDING_REVIEW: { label: "Em análise", className: "bg-amber-100 text-amber-800" },
  DISAPPROVED: { label: "Reprovada", className: "bg-red-100 text-red-800" },
  PREAPPROVED: { label: "Pré-aprovada", className: "bg-amber-100 text-amber-800" },
  PENDING_BILLING_INFO: { label: "Sem forma de pagamento", className: "bg-amber-100 text-amber-800" },
};

export function statusDe(effective: string | null | undefined) {
  return STATUS[effective ?? ""] ?? { label: effective ?? "—", className: "bg-slate-100 text-slate-700" };
}

export const OBJETIVO: Record<string, string> = {
  OUTCOME_LEADS: "Cadastros",
  OUTCOME_SALES: "Vendas",
  OUTCOME_TRAFFIC: "Tráfego",
  OUTCOME_ENGAGEMENT: "Engajamento",
  OUTCOME_AWARENESS: "Reconhecimento",
  OUTCOME_APP_PROMOTION: "App",
};

export const OTIMIZACAO: Record<string, string> = {
  CONVERSATIONS: "Conversas",
  LINK_CLICKS: "Cliques no link",
  POST_ENGAGEMENT: "Engajamento",
  LEAD_GENERATION: "Cadastros",
  OFFSITE_CONVERSIONS: "Conversões",
  REACH: "Alcance",
  IMPRESSIONS: "Impressões",
  LANDING_PAGE_VIEWS: "Visitas à página",
  THRUPLAY: "Visualizações",
};

export function moeda(cents: number | null | undefined, currency: string | null) {
  if (cents == null) return "—";
  const valor = cents / 100;
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency || "BRL",
      maximumFractionDigits: 2,
    }).format(valor);
  } catch {
    return `${valor.toFixed(2)} ${currency ?? ""}`.trim();
  }
}

export const numero = (n: number | null | undefined) =>
  n == null ? "—" : new Intl.NumberFormat("pt-BR").format(n);

export const percentual = (n: number | null | undefined) =>
  n == null ? "—" : `${n.toFixed(2).replace(".", ",")}%`;

/** Rótulo do resultado principal que a Meta reporta. */
export function rotuloResultado(type: string | undefined | null) {
  if (!type) return null;
  if (type.includes("purchase")) return "compras";
  if (type.includes("lead")) return "cadastros";
  if (type.includes("messaging")) return "conversas";
  if (type.includes("landing_page")) return "visitas";
  if (type.includes("link_click")) return "cliques no link";
  if (type.includes("post_engagement")) return "engajamentos";
  return type;
}

/** "R$ 50,00" → 5000; aceita "50", "50,5", "1.234,56". Null se inválido. */
export function reaisParaCentavos(raw: string): number | null {
  let v = raw.trim().replace(/[R$\s]/gi, "");
  if (!v) return null;
  if (v.includes(",")) v = v.replace(/\./g, "").replace(",", ".");
  // Sem vírgula, ponto é separador de milhar quando vem com 3 dígitos depois
  // ("1.234" é mil duzentos e trinta e quatro reais, não um real e vinte).
  else if (/^\d{1,3}(\.\d{3})+$/.test(v)) v = v.replace(/\./g, "");
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function centavosParaReais(cents: number | null | undefined): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2).replace(".", ",");
}

/** Link para o objeto no Gerenciador de Anúncios da Meta. */
export function linkGerenciador(adAccountId: string | null | undefined, objectType: "campaign" | "adset" | "ad", objectId: string): string {
  const act = (adAccountId ?? "").replace(/^act_/, "");
  const param = objectType === "campaign" ? "selected_campaign_ids" : objectType === "adset" ? "selected_adset_ids" : "selected_ad_ids";
  const view = objectType === "campaign" ? "campaigns" : objectType === "adset" ? "adsets" : "ads";
  return `https://adsmanager.facebook.com/adsmanager/manage/${view}?act=${act}&${param}=${objectId}`;
}
